import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BULK_DEVICE_PAGE_SIZE } from '../../constants/pagination.js';
import { useDevices, useIntl } from '../../hooks';
import { useMultiDeviceSunburst } from '../../hooks/useMultiDeviceSunburst.js';
import { DetectionStatus } from '../../types';
import { Button, Card, CardContent, CardHeader, CardTitle } from '../ui';
import { AnalyticsFiltersPanel } from './AnalyticsFiltersPanel.js';
import { DeviceAvailabilityPanel } from './DeviceAvailabilityPanel.js';
import { MultiDeviceSunburstPanel } from './MultiDeviceSunburstPanel.js';
import { SuppressionsReportCard } from './SuppressionsReportCard.js';

const ALL_STATUSES = Object.values(DetectionStatus);

const getYesterdayMidnight = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  const hours = String(yesterday.getHours()).padStart(2, '0');
  const minutes = String(yesterday.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

type AnalyticDevicesListProps = {
  initialDeviceIds?: string[];
  useUrlState?: boolean;
};

export default function AnalyticDevicesList({
  initialDeviceIds,
  useUrlState = true,
}: AnalyticDevicesListProps = {}) {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  const resolveInitialDeviceIds = (): string[] => {
    if (useUrlState) {
      const param = searchParams.get('deviceIds');
      if (param) {
        return param.split(',').filter(Boolean);
      }
    }
    return initialDeviceIds ?? [];
  };

  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>(resolveInitialDeviceIds);

  const [appliedFilters, setAppliedFilters] = useState<{
    from?: string;
    to?: string;
  }>({
    from: getYesterdayMidnight(),
  });

  const [selectedStatuses, setSelectedStatuses] = useState<Set<DetectionStatus>>(
    () => new Set([DetectionStatus.SUPPRESSION_SUCCESS, DetectionStatus.SUPPRESSION_FAILED]),
  );

  const {
    data: devicesPage,
    isLoading,
    error,
    refetch,
  } = useDevices({
    pageSize: BULK_DEVICE_PAGE_SIZE,
  });
  const devices = devicesPage?.devices ?? [];

  const {
    data: sunburstData,
    isLoading: isSunburstLoading,
    isFetching: isSunburstFetching,
    error: sunburstError,
  } = useMultiDeviceSunburst(
    {
      deviceIds: selectedDeviceIds,
      from: appliedFilters.from,
      to: appliedFilters.to,
      status: Array.from(selectedStatuses),
    },
    {
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: selectedStatuses.size > 0 && selectedDeviceIds.length > 0,
      keepPreviousData: true,
    },
  );

  const statusCounts = useMemo(() => {
    const counts = ALL_STATUSES.reduce(
      (acc, status) => {
        acc[status] = 0;
        return acc;
      },
      {} as Record<DetectionStatus, number>,
    );

    // Reset to zero when no devices are selected
    if (selectedDeviceIds.length === 0) {
      return counts;
    }

    if (sunburstData?.statusCounts) {
      Object.entries(sunburstData.statusCounts).forEach(([status, count]) => {
        if (status in counts) {
          counts[status as DetectionStatus] = count;
        }
      });
    }

    return counts;
  }, [sunburstData?.statusCounts, selectedDeviceIds.length]);

  useEffect(() => {
    if (!useUrlState) return;

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (selectedDeviceIds.length > 0) {
          params.set('deviceIds', selectedDeviceIds.join(','));
        } else {
          params.delete('deviceIds');
        }
        return params;
      },
      { replace: true },
    );
  }, [selectedDeviceIds, setSearchParams, useUrlState]);

  useEffect(() => {
    if (!useUrlState) return;

    const param = searchParams.get('deviceIds');
    const urlIds = param ? param.split(',').filter(Boolean) : [];

    setSelectedDeviceIds((prev) => {
      if (prev.sort().join(',') === urlIds.sort().join(',')) return prev;
      return urlIds;
    });
  }, [searchParams, useUrlState]);

  useEffect(() => {
    // shortcut: with more than one page of devices the list is incomplete, so nothing is pruned
    // and the backend 403 stays the safety net; upgrade by fetching every page.
    if (!devicesPage || devicesPage.total > devicesPage.devices.length) return;

    const knownDeviceIds = new Set(devicesPage.devices.map((device) => device.id));
    const isKnown = (deviceId: string) => knownDeviceIds.has(deviceId);

    // With URL state the URL is pruned and flows into the selection through the effect above;
    // pruning the selection directly would be reverted by that effect while the URL is stale.
    if (useUrlState) {
      const urlIds = searchParams.get('deviceIds')?.split(',').filter(Boolean) ?? [];
      if (urlIds.every(isKnown)) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const keptIds = (params.get('deviceIds')?.split(',') ?? []).filter(isKnown);
          if (keptIds.length > 0) {
            params.set('deviceIds', keptIds.join(','));
          } else {
            params.delete('deviceIds');
          }
          return params;
        },
        { replace: true },
      );
      return;
    }

    setSelectedDeviceIds((prev) => (prev.every(isKnown) ? prev : prev.filter(isKnown)));
  }, [devicesPage, searchParams, setSearchParams, useUrlState]);

  if (isLoading) {
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">
            {intl.formatMessage({ id: 'detections.devices' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            {intl.formatMessage({ id: 'detections.loadingDevices' })}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-4">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">
              {intl.formatMessage({ id: 'detections.devices' })}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="text-xs">
              {intl.formatMessage({ id: 'common.retry' })}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-destructive">
            {intl.formatMessage({ id: 'detections.errorLoadingDevices' })}: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <AnalyticsFiltersPanel
        devices={devices}
        selectedDeviceIds={selectedDeviceIds}
        onDeviceIdsChange={setSelectedDeviceIds}
        appliedFilters={appliedFilters}
        onApplyDateFilters={setAppliedFilters}
        selectedStatuses={selectedStatuses}
        onStatusesChange={setSelectedStatuses}
        statusCounts={statusCounts}
        initialFrom={getYesterdayMidnight()}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {intl.formatMessage({ id: 'analytics.sunburstTitle' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MultiDeviceSunburstPanel
            deviceIds={selectedDeviceIds}
            groups={sunburstData?.groups}
            isLoading={isSunburstLoading}
            isFetching={isSunburstFetching}
            error={sunburstError}
          />
        </CardContent>
      </Card>

      <SuppressionsReportCard
        deviceIds={selectedDeviceIds}
        from={appliedFilters.from}
        to={appliedFilters.to}
      />

      <DeviceAvailabilityPanel
        deviceIds={selectedDeviceIds}
        from={appliedFilters.from}
        to={appliedFilters.to}
        devices={devices}
      />
    </div>
  );
}
